import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BlogHeader from '../components/BlogHeader';

// ============================================================
// CRITICAL: Do NOT add blog data to this file.
// Blog posts are stored in the Django database and served via API.
// To create a new blog post, POST to /api/blogs/ on the backend.
// This file only fetches and renders — it contains zero blog content.
// ============================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const CATEGORIES = ['All', 'Productivity', 'Startups', 'Marketing', 'Sales', 'Technology', 'Content', 'AI'];
const STYLES = ['All', 'Thought Leadership', 'Journalistic', 'Educational', 'Philosophical', 'Reflective', 'Documentary', 'Data-Driven', 'SEO', 'Industry', 'Product'];

export default function Blogs() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeStyle, setActiveStyle] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/blogs/`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setPosts(data);
        setLoading(false);
      })
      .catch(err => {
        setError('Could not load blog posts. Please try again later.');
        setLoading(false);
      });
  }, []);

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesStyle = activeStyle === 'All' || (post.style && post.style === activeStyle);
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesStyle && matchesSearch;
  });

  return (
    <div style={{ height: '100vh', overflow: 'auto', background: '#0D1117', fontFamily: 'Inter, sans-serif', color: '#E2E8F0' }}>
      <BlogHeader />

      {/* Header */}
      <div style={{ padding: '100px 24px 20px', textAlign: 'center', maxWidth: 700, margin: '0 auto' }}>
        <div style={{ display: 'inline-block', background: 'rgba(249,115,22,0.15)', color: '#F97316', padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700, marginBottom: 16 }}>BLOG</div>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, marginBottom: 12 }}>Multi Author Blog</h1>
        <p style={{ color: '#9CA3AF', fontSize: 15, marginBottom: 24 }}>Insights on AI content creation, writing workflows, and how 7 specialised agents produce content that rivals human teams.</p>
        
        {/* Search */}
        <div style={{ position: 'relative', maxWidth: 400, margin: '0 auto' }}>
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '12px 16px', background: '#1A2236', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#E2E8F0', fontSize: 14, outline: 'none' }}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '0 24px 20px', maxWidth: 1140, margin: '0 auto' }}>
        {/* Category filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: activeCategory === cat ? '#F97316' : '#1A2236',
                color: activeCategory === cat ? 'white' : '#9CA3AF',
              }}
            >{cat}</button>
          ))}
        </div>
        {/* Style filters */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STYLES.map(style => (
            <button
              key={style}
              onClick={() => setActiveStyle(style)}
              style={{
                padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 500, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)',
                background: activeStyle === style ? 'rgba(249,115,22,0.2)' : 'transparent',
                color: activeStyle === style ? '#F97316' : '#6B7280',
              }}
            >{style}</button>
          ))}
        </div>
      </div>

      {/* Loading / Error states */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#6B7280' }}>
          <p style={{ fontSize: 16 }}>Loading articles...</p>
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#F97316' }}>
          <p style={{ fontSize: 16 }}>{error}</p>
        </div>
      )}

      {/* Results count */}
      {!loading && !error && (
        <div style={{ padding: '0 24px 16px', maxWidth: 1140, margin: '0 auto' }}>
          <p style={{ color: '#6B7280', fontSize: 13 }}>{filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}</p>
        </div>
      )}

      {/* Blog grid */}
      {!loading && !error && (
        <div style={{ padding: '0 24px 80px', maxWidth: 1140, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {filteredPosts.map(post => (
            <div
              key={post.slug}
              onClick={() => navigate(`/blogs/${post.slug}`)}
              style={{ background: '#1A2236', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, cursor: 'pointer', transition: 'border-color 0.2s', display: 'flex', flexDirection: 'column' }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(249,115,22,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
            >
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{post.category}</span>
                {post.style && <span style={{ color: '#64748B', fontSize: 11 }}>{post.style}</span>}
              </div>
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 8, lineHeight: 1.3 }}>{post.title}</h3>
              <p style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 1.6, flex: 1, marginBottom: 12 }}>{post.excerpt}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748B', fontSize: 12 }}>
                <span>{post.date}</span>
                <span>{post.readTime}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredPosts.length === 0 && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#6B7280' }}>
          <p style={{ fontSize: 16 }}>No articles match your filters.</p>
          <button onClick={() => { setActiveCategory('All'); setActiveStyle('All'); setSearchQuery(''); }} style={{ marginTop: 12, padding: '8px 20px', background: '#F97316', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Clear filters</button>
        </div>
      )}
    </div>
  );
}
